import { useCallback, useMemo, useState } from "react";
import {
  Center,
  Group,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { IconClock } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";

import {
  ReportDateRangePicker,
  getLastMonthRange,
  type DateRange,
} from "../../components/reports/ReportDateRangePicker";
import { ReportExportButton } from "../../components/reports/ReportExportButton";
import { usePractices } from "../../hooks/admin/usePractices";
import { usePathologists } from "../../hooks/useReferenceData";
import { useTurnaroundTimeReport } from "../../hooks/useReports";
import { useLabTodayDateOnly } from "../../hooks/useLabTodayDateOnly";
import { listPrefixes } from "../../services/prefixService";
import { reportsService } from "../../services/reports.service";
import { canonicalReportDateRangeParams } from "./reportDateRangeParams";

const DEFAULT_RANGE: DateRange = getLastMonthRange();

export function TurnaroundTimeReportContent() {
  const labToday = useLabTodayDateOnly();
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_RANGE);
  const [pathologistId, setPathologistId] = useState<string | null>(null);
  const [practiceId, setPracticeId] = useState<string | null>(null);
  const [prefixId, setPrefixId] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const { data: pathologists = [] } = usePathologists();
  const { data: practices = [] } = usePractices();
  const fetchPrefixes = useCallback(() => listPrefixes({ all: true }), []);
  const { data: prefixListResponse } = useQuery({
    queryKey: ["prefixes", "all"],
    queryFn: fetchPrefixes,
  });

  const prefixes = useMemo(
    () => prefixListResponse?.data ?? [],
    [prefixListResponse]
  );
  const pathologistOptions = useMemo(
    () =>
      pathologists.map((pathologist) => ({
        value: pathologist.id,
        label: pathologist.name,
      })),
    [pathologists]
  );
  const practiceOptions = useMemo(
    () =>
      practices.map((practice) => ({
        value: practice.id,
        label: practice.name,
      })),
    [practices]
  );
  const prefixOptions = useMemo(
    () => prefixes.map((prefix) => ({ value: prefix.id, label: prefix.code })),
    [prefixes]
  );
  const params = useMemo(
    () => ({
      ...canonicalReportDateRangeParams(dateRange),
      pathologistId: pathologistId ?? undefined,
      practiceId: practiceId ?? undefined,
      prefixId: prefixId ?? undefined,
    }),
    [dateRange, pathologistId, practiceId, prefixId]
  );

  const { data, isLoading } = useTurnaroundTimeReport(params, page);
  const totalPages = useMemo(
    () => (data ? Math.ceil(data.pagination.total / data.pagination.limit) : 1),
    [data]
  );
  const averageTat = useMemo(() => {
    if (!data || data.data.length === 0) return 0;
    const values = data.data
      .map((row) => row.totalTatDays)
      .filter(
        (value): value is number => typeof value === "number" && value >= 0
      );
    if (values.length === 0) return 0;
    const sum = values.reduce(
      (accumulator, current) => accumulator + current,
      0
    );
    return Math.round((sum / values.length) * 10) / 10;
  }, [data]);

  const handlePathologistChange = useCallback((value: string | null) => {
    setPathologistId(value);
    setPage(1);
  }, []);
  const handlePracticeChange = useCallback((value: string | null) => {
    setPracticeId(value);
    setPage(1);
  }, []);
  const handlePrefixChange = useCallback((value: string | null) => {
    setPrefixId(value);
    setPage(1);
  }, []);
  const handleDateRangeChange = useCallback((range: DateRange) => {
    setDateRange(range);
    setPage(1);
  }, []);
  const handleExport = useCallback(
    () => reportsService.exportTurnaroundTime(params),
    [params]
  );

  return (
    <Stack gap="lg">
      <Paper p="md" withBorder>
        <Group justify="space-between" align="flex-end" wrap="wrap">
          <Group align="flex-end" wrap="wrap" gap="md">
            <Select
              label="Pathologist"
              placeholder="All pathologists"
              data={pathologistOptions}
              value={pathologistId}
              onChange={handlePathologistChange}
              searchable
              clearable
              w={180}
            />
            <Select
              label="Practice"
              placeholder="All practices"
              data={practiceOptions}
              value={practiceId}
              onChange={handlePracticeChange}
              searchable
              clearable
              w={180}
            />
            <Select
              label="Case Prefix"
              placeholder="All prefixes"
              data={prefixOptions}
              value={prefixId}
              onChange={handlePrefixChange}
              searchable
              clearable
              w={150}
            />
            <ReportDateRangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
            />
          </Group>
          <ReportExportButton
            filename={`turnaround-time-${labToday}.csv`}
            fetchData={handleExport}
          />
        </Group>
      </Paper>

      {data && (
        <Paper p="md" withBorder>
          <Group>
            <IconClock size={20} color="var(--mantine-color-orange-6)" />
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Average Total TAT
              </Text>
              <Text size="xl" fw={700}>
                {averageTat} Days
              </Text>
            </div>
          </Group>
        </Paper>
      )}

      <Paper withBorder>
        {isLoading ? (
          <Stack p="md" gap="xs">
            {[...Array(5)].map((_, index) => (
              <Skeleton key={index} h={36} />
            ))}
          </Stack>
        ) : !data || data.data.length === 0 ? (
          <Center py="xl">
            <Text c="dimmed">No data for the selected filters.</Text>
          </Center>
        ) : (
          <>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Case #</Table.Th>
                  <Table.Th>Patient</Table.Th>
                  <Table.Th>Practice</Table.Th>
                  <Table.Th>Prefix</Table.Th>
                  <Table.Th>Acc. to Gross (min)</Table.Th>
                  <Table.Th>Gross (min)</Table.Th>
                  <Table.Th>Embed (min)</Table.Th>
                  <Table.Th>Cut (min)</Table.Th>
                  <Table.Th>Review (min)</Table.Th>
                  <Table.Th>Total (Days)</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.data.map((row) => (
                  <Table.Tr key={row.caseId}>
                    <Table.Td>
                      <Text size="sm" c="blue" fw={500}>
                        {row.caseNumber}
                      </Text>
                    </Table.Td>
                    <Table.Td>{row.patientName}</Table.Td>
                    <Table.Td>{row.practiceName ?? "—"}</Table.Td>
                    <Table.Td>{row.prefixCode ?? "—"}</Table.Td>
                    <Table.Td>{row.accessioningTimeMinutes ?? "—"}</Table.Td>
                    <Table.Td>{row.grossingTimeMinutes ?? "—"}</Table.Td>
                    <Table.Td>{row.embeddingTimeMinutes ?? "—"}</Table.Td>
                    <Table.Td>{row.cuttingTimeMinutes ?? "—"}</Table.Td>
                    <Table.Td>{row.reviewTimeMinutes ?? "—"}</Table.Td>
                    <Table.Td fw={600}>
                      {row.totalTatDays != null ? `${row.totalTatDays} d` : "—"}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {totalPages > 1 && (
              <Group justify="center" p="md">
                <Pagination
                  value={page}
                  onChange={setPage}
                  total={totalPages}
                />
              </Group>
            )}
          </>
        )}
      </Paper>
    </Stack>
  );
}
